import React, { useState, useEffect } from 'react';
import { Loader2, ExternalLink, ChevronDown, ChevronRight, Activity, TrendingUp, TrendingDown, Target } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { LegislativeBillAnalysis, CompanyImpact } from '../../services/capitolGeminiService';

import { auth } from '../../firebase';

export function CapitolDashboard({ onAnalyzeTicker }: { onAnalyzeTicker?: (ticker: string) => void }) {
  const [sector, setSector] = useState("Healthcare");
  const [sectors, setSectors] = useState<string[]>([]);
  const [analyses, setAnalyses] = useState<LegislativeBillAnalysis[]>([]);
  const [analysisCache, setAnalysisCache] = useState<Record<string, LegislativeBillAnalysis[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedProvisions, setExpandedProvisions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetch('/api/fmp/capitol/sectors')
      .then(async res => {
         const text = await res.text();
         if (text.trim().startsWith('<')) return [];
         return JSON.parse(text);
      })
      .then(data => {
         if (Array.isArray(data) && data.length > 0) setSectors(data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    // Sticky results: if this sector was already scanned this session, serve
    // the cached analysis instantly — no new server/Gemini call.
    if (analysisCache[sector]) {
      setAnalyses(analysisCache[sector]);
      setError("");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/fmp/capitol/analyze', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ sector })
        });
        if (!res.ok) {
           let errMsg = "Legislative data temporarily unavailable";
           try {
             const htmlCheck = await res.clone().text();
             if (htmlCheck.trim().startsWith('<')) {
                throw new Error("Received HTML, server might be starting up");
             }
             const e = await res.json();
             if (e.error) errMsg = e.error;
           } catch (_) {}
           throw new Error(errMsg);
        }
        const data = await res.json();
        setAnalyses(data);
        setAnalysisCache(prev => ({ ...prev, [sector]: data }));
      } catch (err: any) {
         setError(err.message);
      } finally {
         setLoading(false);
      }
    };
    fetchData();
  }, [sector, analysisCache]);

  const toggleProvisions = (billId: string) => {
    setExpandedProvisions(prev => ({ ...prev, [billId]: !prev[billId] }));
  };

  const handleAnalyze = (ticker: string) => {
     if (onAnalyzeTicker) onAnalyzeTicker(ticker);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-serif font-medium italic tracking-tight">Capitol Edge</h1>
        <p className="text-sm text-ink-500 mt-1 uppercase tracking-wider font-medium">Legislative Alpha Intelligence</p>
      </div>

      {/* Sector Selector */}
      <div className="flex flex-wrap gap-2">
        {sectors.length === 0 ? (
           <div className="animate-pulse flex gap-2">
             {[1,2,3,4,5].map(i => <div key={i} className="h-8 w-24 bg-surface-2 rounded-md" />)}
           </div>
        ) : (
          sectors.map(s => (
            <button
              key={s}
              onClick={() => setSector(s)}
              className={cn(
                "px-4 py-1.5 rounded-md text-xs font-medium uppercase tracking-wider transition-colors",
                sector === s 
                  ? "bg-oxblood text-paper" 
                  : "bg-surface-2 text-ink-600 hover:bg-surface"
              )}
            >
              {s}
            </button>
          ))
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
           <Loader2 className="w-8 h-8 animate-spin text-ink-300 mb-4" />
           <p className="text-ink-500 font-medium text-sm">Scanning Congressional docket and analyzing impact for {sector}...</p>
        </div>
      ) : error ? (
        <div className="bg-danger-50 border border-danger-100 text-danger-600 p-6 rounded-md text-center">
           <p className="font-medium">{error}</p>
        </div>
      ) : analyses.length === 0 ? (
        <div className="oi-card p-12 text-center text-ink-500 flex flex-col items-center gap-4">
           <Target className="w-10 h-10 text-ink-300" />
           <p className="font-medium text-lg text-ink-600">No active legislation found for this sector</p>
        </div>
      ) : (
        <div className="space-y-6">
          {analyses.map(analysis => (
            <div key={analysis.billId} className="oi-card p-6 space-y-6">
              
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-surface-2 text-ink-600 text-[10px] font-medium uppercase tracking-wider rounded-md">
                      {analysis.billId}
                    </span>
                    <span className="px-2 py-1 bg-surface-2 text-ink-600 text-[10px] font-medium uppercase tracking-wider rounded-md border border-ink-200">
                      {analysis.status}
                    </span>
                    <span className={cn(
                      "px-2 py-1 text-[10px] font-medium uppercase tracking-wider rounded-md border",
                      analysis.overallSectorImpact === 'TAILWIND' ? "bg-success-50 text-success-700 border-success-100" :
                      analysis.overallSectorImpact === 'HEADWIND' ? "bg-danger-50 text-danger-700 border-danger-100" :
                      "bg-warning-50 text-warning-700 border-warning-100"
                    )}>
                      {analysis.overallSectorImpact}
                    </span>
                  </div>
                  <h2 className="text-xl font-serif font-medium text-ink leading-tight">
                    {analysis.billTitle}
                  </h2>
                </div>
                
                <div className="md:w-32 shrink-0">
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-[10px] font-medium text-ink-500 uppercase">Passage Prob</span>
                    <span className="text-sm font-medium text-ink">{analysis.passageProbability}%</span>
                  </div>
                  <div className="w-full bg-surface-2 rounded-sm h-2 overflow-hidden">
                    <div 
                      className={cn(
                        "h-full rounded-sm transition-all",
                        analysis.passageProbability >= 65 ? "bg-success-600" :
                        analysis.passageProbability >= 35 ? "bg-warning-600" : "bg-ink-300"
                      )}
                      style={{ width: `${analysis.passageProbability}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div>
                    <h3 className="text-xs font-medium text-ink-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
                      <TrendingUp className="w-3.5 h-3.5 text-success-600" />
                      Beneficiaries
                    </h3>
                    {analysis.beneficiaries?.length > 0 ? (
                      <div className="space-y-3">
                        {analysis.beneficiaries.map(c => (
                          <div key={c.ticker} className="bg-success-50 rounded-md p-3 border border-success-100">
                             <div className="flex items-center justify-between mb-1.5">
                               <div className="flex items-center gap-2">
                                 <span className="font-medium text-success-700 text-sm">{c.ticker}</span>
                                 <span className="text-[10px] bg-success-100 text-success-700 px-1.5 py-0.5 rounded font-medium">{c.impactMagnitude}</span>
                               </div>
                               <button 
                                 onClick={() => handleAnalyze(c.ticker)}
                                 className="text-[10px] font-medium text-ink-500 hover:text-oxblood uppercase tracking-wider flex items-center gap-0.5"
                               >
                                 <Activity className="w-3 h-3" />
                                 Oracle
                               </button>
                             </div>
                             <p className="text-xs text-ink-600 leading-relaxed">{c.rationale}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-ink-500 italic">None identified</p>
                    )}
                 </div>

                 <div>
                    <h3 className="text-xs font-medium text-ink-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
                      <TrendingDown className="w-3.5 h-3.5 text-danger-600" />
                      Harmed
                    </h3>
                    {analysis.harmed?.length > 0 ? (
                      <div className="space-y-3">
                        {analysis.harmed.map(c => (
                          <div key={c.ticker} className="bg-danger-50 rounded-md p-3 border border-danger-100">
                             <div className="flex items-center justify-between mb-1.5">
                               <div className="flex items-center gap-2">
                                 <span className="font-medium text-danger-700 text-sm">{c.ticker}</span>
                                 <span className="text-[10px] bg-danger-100 text-danger-700 px-1.5 py-0.5 rounded font-medium">{c.impactMagnitude}</span>
                               </div>
                               <button 
                                 onClick={() => handleAnalyze(c.ticker)}
                                 className="text-[10px] font-medium text-ink-500 hover:text-oxblood uppercase tracking-wider flex items-center gap-0.5"
                               >
                                 <Activity className="w-3 h-3" />
                                 Oracle
                               </button>
                             </div>
                             <p className="text-xs text-ink-600 leading-relaxed">{c.rationale}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-ink-500 italic">None identified</p>
                    )}
                 </div>
              </div>
              
              <div className="bg-surface-2 rounded-md p-4 border border-ink-200">
                <button 
                  onClick={() => toggleProvisions(analysis.billId)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-xs font-medium text-ink-600 uppercase tracking-wider">Key Provisions</span>
                  {expandedProvisions[analysis.billId] ? <ChevronDown className="w-4 h-4 text-ink-500" /> : <ChevronRight className="w-4 h-4 text-ink-500" />}
                </button>
                {expandedProvisions[analysis.billId] && (
                  <ul className="mt-3 space-y-2 list-disc list-outside ml-4">
                    {analysis.keyProvisions.map((prov, i) => (
                      <li key={i} className="text-sm text-ink-600 leading-relaxed pl-1">{prov}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border-t border-ink-200 pt-4">
                 <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-1.5">Analyst Note</p>
                 <p className="text-sm text-ink-600 font-serif italic leading-relaxed">{analysis.analystNote}</p>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}
